/**
 * NarrativeEngine.js
 * The "Conductor" of the composition.
 * Generates a global tension curve that influences all musical elements over time.
 */

// Helper: Sigmoid-like Easing (Simmer then Spike)
// x^2.5 maps 0.5 -> 0.17, 0.8 -> 0.57, 1.0 -> 1.0
const applyCurve = (value) => {
    return Math.pow(Math.max(0, Math.min(1, value)), 2.5);
};

// Task 1: "Sigmoid Standard" Utility
// Standard logistic function: 1 / (1 + exp(-k * (t - midpoint)))
// t: input (0-1), k: steepness (default 5 for "Musical S-Curve"), midpoint: x-axis center (0.5)
export const sigmoid = (t, k = 5, midpoint = 0.5) => {
    return 1 / (1 + Math.exp(-k * (t - midpoint)));
};

import { Random } from './Random.js';

// Helper: Resolve [min, max] range to a concrete value
const resolveRange = (value, random) => {
    if (Array.isArray(value) && value.length === 2) {
        const min = value[0];
        const max = value[1];
        return min + (random.next() * (max - min));
    }
    return value; // Static number
};

// Directive 1: Profile Library
const TENSION_PROFILES = {
    FLAT: (start, end, t) => start + ((random) => (random - 0.5) * 0.05), // Flat with jitter (handled in caller)
    RAMP_LINEAR: (start, end, t) => start + (t * (end - start)),
    RAMP_SLOW: (start, end, t) => start + (Math.pow(t, 2) * (end - start)), // Convex
    RAMP_SHARP: (start, end, t) => start + (Math.pow(t, 0.5) * (end - start)), // Concave (Fast rise)
    DECAY: (start, end, t) => start + (t * (end - start)), // Linear decay if end < start
    SWELL: (start, end, t) => {
        // Arch: Start -> Peak -> End
        // Peak is max(start, end) + 0.15
        const peak = Math.max(start, end) + 0.15;
        // Parabolic arc approximation: 4 * (peak - base) * (x - x^2) + base
        // Actually, let's use sine for smoothness
        return start + (Math.sin(t * Math.PI) * (peak - start));
        // Note: Logic simplified, assumes end ~= start for perfect swell.
    }
};

const selectTensionProfile = (type, instanceCount, random) => {
    // Weighted Probabilities
    if (type.includes('VERSE')) {
        if (instanceCount === 0) return random.pick(['FLAT', 'RAMP_SLOW']);
        return random.pick(['RAMP_LINEAR', 'RAMP_SLOW', 'DECAY']); // Variation
    }
    if (type.includes('CHORUS')) return 'FLAT'; // High energy plateau
    if (type.includes('BUILD') || type.includes('RISE')) return 'RAMP_SHARP';
    if (type.includes('INTRO')) return random.pick(['RAMP_SLOW', 'SWELL']);
    if (type.includes('OUTRO')) return 'DECAY';
    if (type.includes('BRIDGE')) return 'SWELL';

    return 'RAMP_LINEAR';
};

export const generateTensionCurve = (structure, genre = 'default', seed = 12345) => {
    if (!structure || structure.length === 0) return () => 0.5;

    const random = new Random(seed);

    // Genre Ceilings (Context Ceiling)
    const constraints = {
        max: 1.0,
        min: 0.4 // Commercial Physics: Loud floor (was 0.0)
    };

    if (genre.includes('lofi') || genre.includes('ambient') || genre.includes('chill')) {
        constraints.max = 0.75; // Bumped from 0.6 to allow some dynamics
        constraints.min = 0.45; // Audibility floor
    } else if (genre.includes('cinematic') || genre.includes('metal')) {
        constraints.max = 1.0; // Allow full range
        constraints.min = 0.35; // Allow quieter intros
    }

    // 1. Calculate total duration and section boundaries
    let totalBars = 0;
    const sections = structure.map(s => {
        const start = totalBars;
        const duration = s.bars || 8; // Default 8 bars
        totalBars += duration;
        return { ...s, start, end: start + duration };
    });

    // 2. Define Tension Points per Section (Task 2: Dynamic Range Upgrade)
    // Replaced static floats with parametric ranges to fix "Static Low-Voltage".
    // Added explicit bias keys for Task 3 "Connection Calibration".
    const tensionMap = {
        INTRO: {
            start_range: [0.35, 0.50],
            end_range: [0.45, 0.60],
            melodic_density_bias: -0.3,
            harmonic_complexity_bias: 'simple'
        },
        VERSE: {
            start_range: [0.45, 0.60],
            end_range: [0.55, 0.65],
            melodic_density_bias: -0.1,
            harmonic_complexity_bias: 'moderate'
        },
        PRE_CHORUS: {
            start_range: [0.60, 0.70],
            end_range: [0.75, 0.85],
            melodic_density_bias: 0.1,
            harmonic_complexity_bias: 'complex'
        },
        BUILD: {
            start_range: [0.50, 0.60],
            end_range: [0.85, 0.95],
            melodic_density_bias: 0.0,
            harmonic_complexity_bias: 'simple' // Ramp requires simplicity
        },
        CHORUS: {
            start_range: [0.80, 0.92], // Capped < 0.95 (Clipping Risk)
            end_range: [0.85, 0.92],
            melodic_density_bias: 0.2,
            harmonic_complexity_bias: 'moderate'
        },
        CLIMAX: {
            start_range: [0.90, 0.95],
            end_range: [0.95, 0.98], // Edge of clipping
            melodic_density_bias: 0.3,
            harmonic_complexity_bias: 'complex'
        },
        BRIDGE: {
            start_range: [0.65, 0.55],
            end_range: [0.50, 0.45], // Dip
            melodic_density_bias: 0.1,
            harmonic_complexity_bias: 'complex'
        },
        OUTRO: {
            start_range: [0.60, 0.50],
            end_range: [0.40, 0.30], // Don't hit 0.0 (Silence Bug)
            melodic_density_bias: -0.2,
            harmonic_complexity_bias: 'simple'
        },
        DEFAULT: {
            start_range: [0.5, 0.5],
            end_range: [0.5, 0.5],
            melodic_density_bias: 0.0
        }
    };

    // 3. Pre-calculate the curve points
    const curvePoints = [];
    let prevEndTension = 0.0;
    const instanceCounts = {}; // Track iteration count per type

    sections.forEach((section, idx) => {
        const type = section.type ? section.type.toUpperCase() : 'DEFAULT';

        // Track Instances
        if (!instanceCounts[type]) instanceCounts[type] = 0;
        const instanceIndex = instanceCounts[type];
        instanceCounts[type]++;

        let config = tensionMap.DEFAULT;

        if (type.includes('INTRO')) config = tensionMap.INTRO;
        else if (type.includes('PRE')) config = tensionMap.PRE_CHORUS;
        else if (type.includes('VERSE')) config = tensionMap.VERSE;
        else if (type.includes('BUILD') || type.includes('RISE')) config = tensionMap.BUILD;
        else if (type.includes('CHORUS')) config = tensionMap.CHORUS;
        else if (type.includes('CLIMAX') || type.includes('DROP')) config = tensionMap.CLIMAX;
        else if (type.includes('BRIDGE') || type.includes('BREAK')) config = tensionMap.BRIDGE;
        else if (type.includes('OUTRO')) config = tensionMap.OUTRO;

        // Directive 4: Instance Awareness (Energy Step-Up)
        // If this is Verse 2+, boost the target range
        let instanceBoost = 0.0;
        if (instanceIndex > 0) {
            instanceBoost = 0.05 * instanceIndex; // +0.05 per recurrence
        }

        // Directive 4: The "Strip-Down" Variation (Breakdown Verse)
        let isBreakdown = false;
        if (type.includes('VERSE') && instanceIndex > 0 && random.bool(0.2)) {
             isBreakdown = true;
             instanceBoost = -0.2; // Drop energy
        }

        // Resolve Ranges using Seed
        let targetStart = resolveRange(config.start_range, random) + instanceBoost;
        let targetEnd = resolveRange(config.end_range, random) + instanceBoost;

        // Directive 3: The "Handshake" (Continuity)
        // Ensure start matches previous end (unless it's the first section or a DROP)
        let startTension = (idx === 0) ? targetStart : prevEndTension;

        // Hard Cut Exception for DROP or BREAKDOWN
        if (type.includes('DROP') || isBreakdown) {
             startTension = targetStart; // Disregard continuity
        }

        let endTension = targetEnd;

        // Apply Global Floor/Ceiling
        startTension = Math.max(constraints.min, Math.min(startTension, constraints.max));
        endTension = Math.max(constraints.min, Math.min(endTension, constraints.max));

        // Directive 2: Profile Selection
        const profileType = selectTensionProfile(type, instanceIndex, random);

        // Store resolved points with metadata
        curvePoints.push({
            start: section.start,
            end: section.end,
            startVal: startTension,
            endVal: endTension,
            profile: profileType,
            // Biases are stored here for retrieval by `getSectionContext` if needed
            melodicBias: config.melodic_density_bias || 0,
            harmonicBias: config.harmonic_complexity_bias || 'moderate'
        });

        prevEndTension = endTension;
    });

    // 4. Return a function that calculates tension at any given Bar
    return (barIndex) => {
        // Find current section data
        const point = curvePoints.find(p => barIndex >= p.start && barIndex < p.end);

        if (!point) {
            // Out of bounds? Return 0 or clamp to last section
            return 0.0;
        }

        // Linear Progress within the section
        const sectionDuration = point.end - point.start;
        const progress = (barIndex - point.start) / sectionDuration; // 0.0 to 1.0

        // Apply Profile Logic
        let calculatedTension = point.startVal;

        // Profiles from Directive 1
        if (point.profile === 'FLAT') {
            calculatedTension = point.startVal + (random.next() * 0.02 - 0.01); // Minor jitter
        } else if (point.profile === 'SWELL') {
             // Swell Logic: Rise to Peak and Fall
             // Use sine wave 0 to PI
             const peak = Math.max(point.startVal, point.endVal) + 0.1;
             calculatedTension = point.startVal + (Math.sin(progress * Math.PI) * (peak - point.startVal));
        } else if (point.profile === 'RAMP_SHARP') {
             calculatedTension = point.startVal + (Math.pow(progress, 0.5) * (point.endVal - point.startVal));
        } else if (point.profile === 'RAMP_SLOW') {
             calculatedTension = point.startVal + (Math.pow(progress, 2.0) * (point.endVal - point.startVal));
        } else {
             // Linear / Decay
             calculatedTension = point.startVal + (progress * (point.endVal - point.startVal));
        }

        // Task 1: The "Curve Audit" (Exponential Mapping) - Still apply global curve?
        // Yes, to map 0-1 linear space to "Musical Energy".
        const curvedTension = applyCurve(calculatedTension);

        return Math.max(0.0, Math.min(1.0, curvedTension));
    };
};
